/**
 * BPI computed-value resolver (Jose 2026-09-27) — turns REAL data into the 0-100 values the BPI engine
 * scores, so the "computed" parameters (revenue/retention/ticket/retail/rating/speed/no-show/new-clients)
 * are never hand-slid. Pulls what the growth snapshot already computed (occupancy/returning/rebooking/
 * retail/rating — real, roster-backed) and derives the rest from the barber's recent appointments.
 *
 * Returns a value per COMPUTED param key, or null when there's no honest data yet (e.g. rating before any
 * reviews, speed before any timed checkouts). The engine drops nulls and renormalizes — never fabricates.
 * BPI-PIN: shared/bpi.ts computeBpi + project-slik-barber-profile-growth-wiring-2026-09-27.
 */
import { DEFAULT_BPI_CONFIG, type BpiConfig, type BpiComputedSource } from '../shared/bpi';
import type { Appointment } from './appointmentService';
import type { Barber } from './barberDataService';

// Normalization targets for the two $/count metrics (tunable later via config; kept explicit + honest).
const TARGET_AVG_TICKET = 50;      // $ that reads as 100%
const TARGET_NEW_CLIENTS_MONTH = 20; // new clients/month that reads as 100%

const BOOKED = new Set(['completed', 'paid']);
const NON_ATTENDING = new Set(['cancelled']);

function clamp100(n: number): number { return Math.max(0, Math.min(100, n)); }

/** Resolve every COMPUTED param in the config to a 0-100 value (or null = no data yet). */
export function resolveBpiComputedValues(
  barber: Barber & { growth?: any },
  appts: Appointment[],
  now: Date = new Date(),
): Record<string, number | null> {
  const g = barber.growth || {};
  const comp = g.components || {};

  // Growth snapshot components are already real 0-100 (roster-backed). Rating is 0 when neutralized
  // (too few reviews) — surface that as null ("awaiting reviews"), not a scored 0.
  const bySource: Partial<Record<BpiComputedSource, number | null>> = {
    occupancy: numOrNull(comp.occupancy),
    returning: numOrNull(comp.returning),
    rebooking: numOrNull(comp.rebooking),
    retail: numOrNull(comp.retail),
    rating: comp.rating && comp.rating > 0 ? clamp100(comp.rating) : null,
  };

  // Appointment-derived metrics over the last ~90 days.
  const since = now.getTime() - 90 * 86400000;
  const recent = appts.filter(a => {
    const t = a.date ? Date.parse(a.date) : NaN;
    return isFinite(t) && t >= since;
  });
  const attended = recent.filter(a => BOOKED.has(a.status as string));

  // Average ticket → normalize vs target.
  if (attended.length) {
    const avgTicket = attended.reduce((s, a) => s + (a.price || 0), 0) / attended.length;
    bySource.avgTicket = clamp100((avgTicket / TARGET_AVG_TICKET) * 100);
  } else {
    bySource.avgTicket = null;
  }

  // Service speed → scheduled ÷ actual, capped at 100 (faster than booked isn't "better" beyond on-time).
  const timed = attended.filter(a => (a as any).actualDuration > 0 && (a.duration || 0) > 0);
  if (timed.length) {
    const speeds = timed.map(a => clamp100(((a.duration || 0) / (a as any).actualDuration) * 100));
    bySource.serviceSpeed = Math.round(speeds.reduce((s, v) => s + v, 0) / speeds.length);
  } else {
    bySource.serviceSpeed = null;
  }

  // No-show rate → % of scheduled-and-reached appts that were no-shows (raw; engine inverts when scored).
  const noShows = recent.filter(a => a.status === 'no-show').length;
  const denom = attended.length + noShows; // exclude cancellations (client cancelled ahead)
  bySource.noShowRate = denom > 0 ? clamp100((noShows / denom) * 100) : null;

  // New clients → count first-timers in the window, normalize vs target (track-only by default).
  const newClients = recent.filter(a => (a as any).isNewClient === true && !NON_ATTENDING.has(a.status as string)).length;
  bySource.newClients = recent.length ? clamp100((newClients / TARGET_NEW_CLIENTS_MONTH) * 100 * (30 / 90) * 3) : null;
  // (window is ~90d ≈ 3 months → compare the 90d count against 3× the monthly target)

  // Punctuality: needs the time clock (not wired yet). Off by default; leave unresolved.
  // bySource remains without a punctuality entry → null below.

  // Map source-keyed values back to PARAM keys for every computed param in the config.
  const out: Record<string, number | null> = {};
  for (const p of DEFAULT_BPI_CONFIG.params) {
    if (p.kind === 'computed' && p.source) {
      out[p.key] = bySource[p.source] ?? null;
    }
  }
  return out;
}

/** Same, but honoring a possibly-customized config (owner may have added computed params). */
export function resolveForConfig(
  config: BpiConfig,
  barber: Barber & { growth?: any },
  appts: Appointment[],
  now: Date = new Date(),
): Record<string, number | null> {
  const base = resolveBpiComputedValues(barber, appts, now);
  // Re-key onto the live config so custom computed params sharing a known source also resolve.
  const bySourceValue = (src?: BpiComputedSource): number | null => {
    const hit = DEFAULT_BPI_CONFIG.params.find(p => p.kind === 'computed' && p.source === src);
    return hit ? base[hit.key] ?? null : null;
  };
  const out: Record<string, number | null> = {};
  for (const p of config.params) {
    if (p.kind === 'computed') out[p.key] = p.source ? bySourceValue(p.source) : null;
  }
  return out;
}

function numOrNull(v: any): number | null {
  return typeof v === 'number' && isFinite(v) ? clamp100(v) : null;
}
